import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useEffectOnce } from 'react-use';
import NextLink from 'next/dist/client/link';
import { sortBy, prop, toLower, compose, propEq } from 'ramda';

import { COLORS } from '@theme/colors';
import { useMonogram } from '@hooks/useMonogram';
import { MONOGRAM_APPEARANCE_TYPE } from '@components/monogram/monogram.constants';
import { getAlphabetDivision, Letter } from '@components/letter';
import { eBaseUrl } from '@components/letter/letter.types';
import { Gallery } from '@definitions/galleries.types';

import {
  Container,
  GalleriesList,
  GalleryLink,
  CurrentGalleries,
  GalleriesCopy,
  AllGalleries,
  AlphabetContainer,
} from './galleriesView.styles';

export interface GalleriesViewProps {
  galleries: Gallery[];
}

export const GalleriesView = ({ galleries }: GalleriesViewProps) => {
  const { addMonogram, clearMonogram } = useMonogram();
  const alphabet = getAlphabetDivision(galleries);

  useEffectOnce(() => {
    const id = new Date().getTime();

    addMonogram({
      letter: 'G',
      color: COLORS.PORTICA,
      appearance: MONOGRAM_APPEARANCE_TYPE.DISAPPEAR,
      id,
    });

    return () => {
      clearMonogram(id);
    };
  });

  const renderFeaturedGalleries = () => (
    <GalleriesList>
      {sortBy(compose(toLower, prop('name')), galleries)
        .filter(propEq('featured', true), galleries)
        .map(({ slug, name }, index) => (
          <GalleryLink
            key={index}
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, '', `/${eBaseUrl.GALLERIES}/${slug}`);
              window.location.href = `/${eBaseUrl.GALLERIES}/${slug}`;
            }}
            href={`/${eBaseUrl.GALLERIES}/${slug}`}
          >
            {name}
          </GalleryLink>
        ))}
    </GalleriesList>
  );

  const renderAllGalleries = () => (
    <AlphabetContainer>
      {sortBy(compose(toLower, prop('initial')), alphabet).map((letter) => (
        <Letter key={letter.initial} letter={letter} baseUrl={eBaseUrl.GALLERIES} />
      ))}
    </AlphabetContainer>
  );

  return (
    <Container>
      <CurrentGalleries>
        <GalleriesCopy>
          <FormattedMessage id="galleriesView.currentGalleries" defaultMessage="Current:" />
        </GalleriesCopy>
      </CurrentGalleries>

      {renderFeaturedGalleries()}

      <AllGalleries>
        <GalleriesCopy>
          <FormattedMessage id="galleriesView.allGalleries" defaultMessage="All:" />
        </GalleriesCopy>
      </AllGalleries>

      {renderAllGalleries()}
    </Container>
  );
};
